import type { PostHog } from 'posthog-js';
import { SessionI } from '@/types/cyclone/requests';
import { HANDOFF_LANDED_SOURCE_KEY } from '@/auth/perfTokenAuth';
import { captureFirstTouch } from './firstTouch';

// Single PostHog project for all tracking (shared with Nightwing).
const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY || '';
const POSTHOG_HOST = import.meta.env.VITE_POSTHOG_HOST || 'https://us.i.posthog.com';

export interface CustomProps {
  screen: string;
  [propName: string]: unknown;
}

const isProd = (): boolean => import.meta.env.VITE_NODE_ENV === 'production';

/**
 * God-mode / impersonation guard. When a support/QA member is impersonating a
 * vendor (god-mode), `vendorUsernameGodMode` holds the impersonated username in
 * localStorage. We never emit analytics in that state so support testing doesn't
 * pollute the real vendor's data.
 */
const isImpersonating = (): boolean => {
  try {
    const raw = localStorage.getItem('vendorUsernameGodMode');
    return !!raw && raw !== 'null' && raw !== '""';
  } catch {
    return false;
  }
};

/** Whether we should actually send events to PostHog (vs. console-log in dev). */
const canTrack = (): boolean => isProd() && !!POSTHOG_KEY && !isImpersonating();

const getUTMParameters = (): Record<string, string> => {
  if (typeof window === 'undefined') return {}; // Ensure this runs in the browser

  const searchParams = new URLSearchParams(window.location.search);
  const utmParams = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];
  const params: Record<string, string> = {};

  utmParams.forEach((param) => {
    const value = searchParams.get(param);
    if (value) params[param] = value;
  });

  return params;
};

// Browser-noise exceptions that carry no actionable signal — dropped before they
// ever reach PostHog (keeps error tracking clean + saves ingestion quota):
//   • ResizeObserver loop … — benign layout-reflow warning, never a real bug
//   • Script error. — opaque cross-origin error with no stack/message
//   • Non-Error promise rejection captured — a rejected promise with a non-Error value
const EXCEPTION_NOISE = [
  'ResizeObserver loop',
  'Script error.',
  'Non-Error promise rejection captured',
  // Client-side network/connectivity failures (user offline, request aborted,
  // tab closed mid-flight). Not actionable from the frontend — real API outages
  // are caught server-side and surfaced in Slack #incidents.
  'Failed to fetch',
  'Load failed',
  'NetworkError when attempting to fetch resource',
  // Third-party scripts we don't control:
  'Could not load "', // Google Maps JS API loader (maps/api/js)
  'Java object is gone', // Android WebView keyboard bridge
  'window.webkit.messageHandlers', // iOS in-app WebView (WKWebView) bridge probe — undefined in mobile Safari
  '/maps/api/js' // runtime errors thrown from inside Google Maps' own minified code
];

const isNoiseException = (properties?: Record<string, unknown>): boolean => {
  if (!properties) return false;
  const messages: string[] = [];
  const list = properties.$exception_list;
  if (Array.isArray(list)) {
    for (const ex of list) {
      if (ex?.value) messages.push(String(ex.value));
      if (ex?.type) messages.push(String(ex.type));
      // Stack-frame sources let us drop errors thrown from inside third-party
      // origins (e.g. Google Maps) regardless of their minified message.
      const frames = ex?.stacktrace?.frames;
      if (Array.isArray(frames)) {
        for (const f of frames) {
          if (f?.source) messages.push(String(f.source));
        }
      }
    }
  }
  if (properties.$exception_message) messages.push(String(properties.$exception_message));
  const haystack = messages.join(' ');
  return EXCEPTION_NOISE.some((p) => haystack.includes(p));
};

// $web_vitals counts as a normal event against the 1M/mo tier. A small sample is
// enough to compute per-route p75 Core Web Vitals, so keep a slice and drop the rest
// in before_send. Tune freely (1 = keep all, 0 = none). Bajado del 5% al 2,5% el
// 10/sep/2026 (~19,9k → ~10k/mes): los 6 insights de CWV leen p75 por ruta sobre 30
// días, y con la mitad de la muestra las rutas del panel siguen dando cientos de
// muestras. La única que se ajusta al filo es `CLS por ruta`, que pide
// `HAVING count() >= 5` sobre 7 días — las rutas de cola larga pueden caerse de esa
// tabla. Si pasa, ampliar esa ventana a 14 días antes que volver a subir el rate.
const WEB_VITALS_SAMPLE_RATE = 0.025;

// Rutas donde SÍ queremos telemetría de clicks (heatmaps + $dead_click): la landing paga
// y todo el alta. Se evalúa una sola vez, al init — igual que el resto de estas opciones.
const CLICK_TELEMETRY_PATHS = /^\/(comenzar|onboarding|setup)/;

// Eventos de CERO valor analítico que se dropean ANTES de ingestar — en before_send,
// que ve TODO evento sin importar qué helper lo disparó (a diferencia de
// SUPPRESSED_EVENTS, que solo cubre trackEvent y se bypasseaba por otros helpers que
// llaman posthog.capture directo). Esto solo es para mantenernos bajo el free tier
// de 1M/mes — juntos eran ~425k eventos/mes de basura:
//   $opt_in / $opt_out — internos de PostHog; el toggle de god-mode los disparaba en
//     cada cambio de ruta (~280k/mes) pese a captureEventName:false.
//   sw_active — medición de adopción del SW ya terminada (~100k/mes).
//   menu_item_clicked — redundante con $pageview (~45k/mes).
//   analytics_dashboard_viewed — redundante con el $pageview nativo de /analytics
//     (~118k/mes; disparaba 4.8x/persona por re-render). La adopción de Analítica
//     se ve igual en Web Analytics (top pages).
//   Batch jul/2026 — interacciones de cero-a-bajo valor, redundantes con outcomes
//   ya trackeados. Sumadas para volver bajo el free tier junto al fix de $set:
//   landing_auth_modal_closed — cierre de modal en landing, sin señal.
//   landing_navbar_item_clicked — clicks de navbar, redundante con $pageview.
//   journey_working_hours_* — toggles granulares del onboarding de horarios; el
//     resultado ya lo captura working_hours_updated.
const DROP_EVENTS = new Set([
  '$opt_in',
  '$opt_out',
  'sw_active',
  'menu_item_clicked',
  'analytics_dashboard_viewed',
  'landing_auth_modal_closed',
  'landing_navbar_item_clicked',
  'journey_working_hours_quick_add_day_toggled',
  'journey_working_hours_time_slot_updated',
  'journey_working_hours_time_slot_added',
  //   Batch sep/2026 — el aviso de PostHog llegó al 80% del free tier a 13 días del
  //   corte del ciclo, con un run-rate proyectado de ~1,41M/mes:
  //   billing_alert_closed — cerrar el aviso de cobro no responde ninguna pregunta;
  //     la que importa es billing_alert_shown, que queda al 100%. Mismo criterio con
  //     el que ya se dropeó landing_auth_modal_closed (~2,5k/mes).
  //   onboarding_plan_staff_count_changed — el stepper emite por CLICK, 7,4 por
  //     persona; el valor final del plan ya viaja en activation_step_completed
  //     (~2,2k/mes).
  'billing_alert_closed',
  'onboarding_plan_staff_count_changed'
]);

// Ninguna URL con credenciales o PII llega a analytics. El fragment completo (#...)
// ya lo corta disable_capture_url_hashes (los tokens del callback de Auth0 viajan ahí:
// /callback#access_token=JWT quedó grabado en ~6.5k eventos / ~2k personas hasta jul/2026);
// esto cubre además las variantes por QUERY string (?email= del prefill de campañas,
// Eventos legítimos pero de frecuencia alta por persona, donde una muestra alcanza
// para leer adopción/tendencia y el conteo exacto no alimenta ningún embudo ni
// dashboard (verificado contra multiplex growth/pulso e insights guardados,
// jul/2026). Cada evento sale con `sample_rate` para poder extrapolar. NO poner
// acá nada que use un embudo de activación o el growth de multiplex.
const SAMPLED_EVENTS: Record<string, number> = {
  blocked_hours_created: 0.1, // ~26k/mes; un vendor bloqueando su agenda lo dispara por cada bloque
  // 1 por arranque de app con sesión sana (~4-5k/día = ~150k/mes a full). Solo se
  // usa como denominador/contraste de storage contra auth_bootstrap_failed, así que
  // una muestra chica alcanza y sobra: al 2% quedan ~120/día, de sobra para leer un
  // ratio que se mira por semana. El evento de FALLA va al 100%: es raro
  // (~100-200/día) y es el que responde la pregunta.
  auth_bootstrap_ok: 0.02,
  // Batch sep/2026, mismo recorte que el bloque de DROP_EVENTS. Todos son telemetría
  // de UI —una vista, un render, un reorder—, ninguno alimenta un embudo ni un insight
  // guardado (contrastado contra system.insights el 10/sep). Los OUTCOMES de negocio
  // del mismo rango de volumen (client_contacted 16,2k/mes, service_edited 13,6k,
  // booking_payment_confirmed 32,2k) quedan deliberadamente al 100%: samplear un
  // outcome rompe la lectura de cuánto pasó, no solo la de la tendencia.
  journey_service_creation_substep_viewed: 0.25, // ~13,9k/mes, 11,2 por persona
  activation_step_viewed: 0.25, // ~11,6k/mes; el embudo de activación usa _completed
  route_content_ready: 0.25, // ~7,1k/mes, telemetría de pintado
  subtitle_content_edited: 0.25, // ~2,2k/mes, 8,3 por persona (dispara por edición)
  services_reordered: 0.25, // ~1,9k/mes, 7,1 por persona (uno por drag)
  checklist_task_opened: 0.5 // ~2,8k/mes
};

// Sampling por SUPERFICIE — para el evento cuyo costo lo hace UNA superficie y las
// otras son baratas, donde un rate plano castigaría a la barata sin necesidad.
// `live_card_viewed` nació el 26/ago/2026 y en dos semanas fue el 18% de TODO el
// proyecto (~256k/mes, el segundo evento más caro después de $pageview):
//   hover        — 142 eventos por persona. Pasar el mouse por un turno de la agenda
//                  no es intención, es el mouse. Al 5% quedan ~5,7k/mes.
//   action_dialog— 43 por persona, uno por apertura del detalle. Al 10%, ~13,5k/mes.
//   profile      — 6 por persona (~7,3k/mes). Va ENTERO, no vale la pena tocarlo.
// La pregunta que contestan ("¿la ficha viva se ve?") se responde igual con muestra,
// y cada evento sale con `sample_rate` para extrapolar. Ningún insight guardado usa
// este evento (verificado en system.insights el 10/sep/2026).
// Segundo recorte (5/oct/2026, ciclo 22/sep–5/oct): aun sampleado seguía en ~26k/mes
// — action_dialog al 10% daba 6,2k filas en 13 días (≈62k aperturas reales) y hover
// al 5% 2,5k. action_dialog baja a 2,5% (~1,5k muestras cada 13 días, de sobra para
// leer adopción por semana) y hover a 1% (~500). Ningún insight guardado lo usa
// (re-verificado el 5/oct). profile sigue entero.
const SAMPLED_SURFACES: Record<string, Record<string, number>> = {
  live_card_viewed: { hover: 0.01, action_dialog: 0.025 }
};

// ?code=&state= del callback OAuth de MercadoPago, ?ott= del autologin) como defensa en
// profundidad. Solo toca props escalares — jamás $snapshot_data.
const SENSITIVE_URL_PARAMS =
  /([?&#])(access_token|id_token|refresh_token|token|code|state|nonce|email|ott|otl)=[^&#]*/gi;
const URL_PROPS = [
  '$current_url',
  '$referrer',
  '$initial_current_url',
  '$initial_referrer',
  '$session_entry_url'
];

const scrubUrlProps = (obj?: Record<string, unknown>): void => {
  if (!obj) return;
  for (const key of URL_PROPS) {
    const value = obj[key];
    if (typeof value === 'string' && value.includes('=')) {
      obj[key] = value.replace(SENSITIVE_URL_PARAMS, '$1$2=REDACTED');
    }
  }
};

// posthog-js se importa DINÁMICO (lazy) para sacarlo del bundle eager (~213KB).
// Se carga en el primer initAnalytics/initPublicAnalytics (route change temprano).
// Los helpers encolan mientras llega el chunk: un evento del funnel de activación
// nunca es descartable solo por haber ocurrido en los primeros ms del boot.
// `posthog` no-null = cliente inicializado (reemplaza el viejo flag isInitialized).
let posthog: PostHog | null = null;
let posthogLoading: Promise<void> | null = null;

// `posthog-js` se carga lazy, pero los efectos de las rutas hijas corren antes que
// el efecto de RouterContent que llama initAnalytics(). Sin una cola, una entrada
// fría a onboarding/setup emitía contra `posthog === null` y el evento se perdía
// para siempre (los wizards además guardan su dedup en sessionStorage).
//
// La cola conserva el orden causal y queda deliberadamente acotada: analytics no
// puede crecer sin límite si el chunk falla por red o por un deploy viejo.
interface PendingCapture {
  name: string;
  properties: Record<string, unknown>;
}

const MAX_PENDING_CAPTURES = 200;
const pendingCaptures: PendingCapture[] = [];
let analyticsReady = false;
let activationRecordingRequested = false;

const captureOrQueue = (name: string, properties: Record<string, unknown>): void => {
  if (!canTrack()) return;

  if (posthog && analyticsReady) {
    posthog.capture(name, properties);
    return;
  }

  if (pendingCaptures.length >= MAX_PENDING_CAPTURES) pendingCaptures.shift();
  pendingCaptures.push({ name, properties });
};

const flushPendingCaptures = (): void => {
  if (!posthog || !analyticsReady) return;
  if (!canTrack()) {
    pendingCaptures.length = 0;
    return;
  }

  const queued = pendingCaptures.splice(0);
  queued.forEach(({ name, properties }) => posthog?.capture(name, properties));
};

/**
 * Emite un evento que tiene que sobrevivir a un `window.location.assign`
 * inmediato. `capture()` normal encola en el RequestQueue de posthog-js (poll de
 * segundos), así que la navegación se lo come: por eso el rebote a /login nunca
 * dejó rastro de POR QUÉ rebotaba. Acá forzamos `send_instantly` + `sendBeacon`,
 * que el browser garantiza aunque el documento se esté yendo.
 *
 * Espera acotada a que PostHog termine de cargar (el chunk es dinámico y este
 * camino puede correr antes). Best-effort puro: nunca tira, nunca bloquea más de
 * `timeoutMs`, y jamás puede frenar la navegación.
 */
export const trackEventBeacon = async (
  name: string,
  customProps: Record<string, unknown>,
  timeoutMs = 800
): Promise<void> => {
  if (!canTrack()) {
    console.log('Event (beacon):', name, customProps);
    return;
  }

  const deadline = Date.now() + timeoutMs;
  while ((!posthog || !analyticsReady) && Date.now() < deadline) {
    await new Promise((resolve) => setTimeout(resolve, 50));
  }
  if (!posthog || !analyticsReady) return;

  try {
    posthog.capture(name, customProps, {
      send_instantly: true,
      transport: 'sendBeacon',
      skip_client_rate_limiting: true
    });
  } catch {
    // El diagnóstico jamás puede romper el arranque ni frenar el rebote.
  }
};

const syncActivationRecording = (): void => {
  if (!posthog || !analyticsReady || !canTrack() || !activationRecordingRequested) return;
  posthog.startSessionRecording();
};

// Shared init options for both the authenticated app (initAnalytics) and public
// pages like the campaign landing (initPublicAnalytics). See initAnalytics for
// the rationale behind each flag (events-only, cross-subdomain, web-vitals RUM).
const POSTHOG_INIT_OPTIONS = {
  api_host: POSTHOG_HOST,
  ui_host: 'https://us.posthog.com',
  autocapture: false,
  capture_pageview: false,
  capture_pageleave: location.pathname === '/comenzar' || location.pathname === '/comenzar-up',
  // Clickmap + dead clicks en /comenzar Y en el alta (wizard + setup). Con autocapture
  // apagado no teníamos forma de saber DÓNDE toca la gente: 0 filas de heatmap, 0
  // $rageclick, 0 $dead_click, así que "tocó algo que no hacía nada" era
  // indistinguible de "no tocó nada". Eso dejó sin resolver una pregunta concreta: de
  // los que ven el paso del plan y no lo completan, el 57% se va sin emitir NINGÚN
  // evento, y sin telemetría de clicks eso es igual de compatible con "vio el precio y
  // se fue" que con "la pantalla no le respondía". `capture_heatmaps` batchea cada 5s y
  // `$dead_click` solo emite cuando un click no produce efecto, así que el costo
  // contra el free tier es marginal; explícito en `false` para el resto de la app
  // para no depender del remote config. Nada de esto prende autocapture.
  capture_heatmaps: CLICK_TELEMETRY_PATHS.test(location.pathname),
  capture_dead_clicks: CLICK_TELEMETRY_PATHS.test(location.pathname),
  disable_session_recording: true,
  disable_surveys: true,
  cross_subdomain_cookie: true,
  // Corta el fragment (#...) de TODAS las URLs capturadas (eventos, person/session props
  // y replay). Los tokens del callback de Auth0 viajan en el hash — sin esto quedaban
  // grabados en $current_url / $initial_current_url. Flash rutea por path: no pierde nada.
  disable_capture_url_hashes: true,
  capture_performance: {
    web_vitals: true,
    // Atribución de CLS: adjunta `attribution.largestShiftTarget` (el selector del
    // elemento que más shifteó) dentro de `$web_vitals_CLS_event` → priorización
    // data-driven del CLS en prod (qué componente reflowea, no adivinar). Carga el
    // bundle de web-vitals con atribución (~12KB) DENTRO del chunk lazy de posthog
    // → 0 costo en el bundle eager. posthog-js lo soporta nativo (web_vitals_attribution).
    // Nota: la atribución retiene refs a nodos del DOM del último shift hasta reportar
    // (acotado, no un leak creciente); si molesta la memoria en sesiones largas se
    // apaga poniendo esto en false. Prendido para la campaña de remediación de CLS.
    web_vitals_attribution: true,
    network_timing: false
  },
  persistence: 'localStorage+cookie' as const,
  before_send: (event: any) => {
    if (!event) return event;
    if (DROP_EVENTS.has(event.event)) return null;
    if (event.event === '$exception' && isNoiseException(event.properties)) return null;
    if (event.event === '$web_vitals' && Math.random() > WEB_VITALS_SAMPLE_RATE) return null;
    scrubUrlProps(event.properties);
    scrubUrlProps(event.properties?.$set);
    const sampleRate = SAMPLED_EVENTS[event.event];
    if (sampleRate !== undefined) {
      if (Math.random() > sampleRate) return null;
      event.properties = { ...event.properties, sample_rate: sampleRate };
    }
    const surfaceRates = SAMPLED_SURFACES[event.event];
    if (surfaceRates) {
      // Una superficie sin rate declarado va al 100% — el default es NO samplear.
      const surfaceRate = surfaceRates[String(event.properties?.surface)];
      if (surfaceRate !== undefined) {
        if (Math.random() > surfaceRate) return null;
        event.properties = { ...event.properties, sample_rate: surfaceRate };
      }
    }
    scrubUrlProps(event.properties?.$set_once);
    scrubUrlProps(event.$set);
    scrubUrlProps(event.$set_once);
    return event;
  }
};

// Lazy-load + init de posthog-js, una sola vez (idempotente). El chunk de posthog
// sale del bundle eager y se baja recién acá. startExceptionAutocapture se engancha
// al inicializar, igual que antes.
const loadPosthog = (): Promise<void> => {
  if (posthog) return Promise.resolve();
  if (!posthogLoading) {
    posthogLoading = import('posthog-js')
      .then((m) => {
        posthog = m.default;
        posthog.init(POSTHOG_KEY, POSTHOG_INIT_OPTIONS);
        posthog.startExceptionAutocapture();
        // Snapshot first-touch origin as super-properties BEFORE any capture, so the
        // signup event on the OAuth-return /callback carries the true origin instead
        // of accounts.google.com. See firstTouch.ts.
        captureFirstTouch(posthog);
      })
      .catch((error) => {
        // Permite reintentar en el próximo cambio de ruta si el chunk falló por una
        // condición transitoria (offline, deploy en curso, CDN). La cola se conserva.
        analyticsReady = false;
        posthog = null;
        posthogLoading = null;
        throw error;
      });
  }
  return posthogLoading;
};

const ignorePosthogLoadFailure = (): void => {
  // loadPosthog ya deja el singleton en estado reintentable. Consumimos el rechazo
  // para no producir un unhandledrejection (que además ensuciaría exception capture).
};

/**
 * Initialize PostHog. Only runs for an authenticated vendor in production.
 *
 * Events-only configuration — no session recording, no autocapture, no
 * automatic pageviews. We emit explicit events only.
 *
 * cross_subdomain_cookie shares the anonymous distinct_id across .agora.red
 * (pro.agora.red ↔ agora.red), so Flash and Nightwing correlate the same
 * visitor pre-signup natively — no manual device_id cookie sync needed.
 *
 * Called on every route change (init idempotente vía loadPosthog). Re-evaluates the
 * god-mode opt-out each time so toggling impersonation takes effect immediately.
 */
export const initAnalytics = (session: SessionI | null): void => {
  if (typeof window === 'undefined' || !session) return;

  if (!isProd() || !POSTHOG_KEY) {
    if (!isProd()) console.log('Analytics: dev mode, PostHog not initialized');
    return;
  }

  // Recorder does NOT auto-start (disable_session_recording). Solo el funnel de
  // activación graba on-demand vía startActivationRecording(). init + exception
  // autocapture ocurren DENTRO de loadPosthog (una sola vez).
  void loadPosthog()
    .then(() => {
      if (!posthog) return;
      // God-mode is already excluded via canTrack() (capture only runs through our
      // guarded helpers; autocapture/pageview are off). The opt toggle below is
      // belt-and-suspenders + recovery from a prior god-mode opt-out — guarded so it
      // NEVER re-fires an $opt_in event on every route change (that flooded volume).
      if (isImpersonating()) {
        posthog.opt_out_capturing();
        analyticsReady = false;
        pendingCaptures.length = 0;
        posthog.stopSessionRecording();
        return;
      }
      if (posthog.has_opted_out_capturing()) {
        posthog.opt_in_capturing({ captureEventName: false });
      }

      // Identificar ANTES de drenar la cola deja todos los eventos tempranos ligados
      // al vendor desde el origen (PostHog igualmente haría el merge anónimo, pero no
      // necesitamos depender de esa reconciliación).
      analyticsReady = true;
      identifyUser(session);
      flushPendingCaptures();
      syncActivationRecording();

      // One-shot: el vendor aterrizó vía handoff con `?src=` (ej. directorio lex_luthor).
      // Emitimos acá — con posthog ya cargado — para cerrar el funnel marketplace → Flash.
      // El distinct_id se comparte por cross_subdomain_cookie en .agora.red, así que
      // PostHog correlaciona el CTA click en market.agora.red con este aterrizaje.
      try {
        const landedSource = window.sessionStorage.getItem(HANDOFF_LANDED_SOURCE_KEY);
        if (landedSource) {
          window.sessionStorage.removeItem(HANDOFF_LANDED_SOURCE_KEY);
          trackEvent('flash_handoff_landed', { source: landedSource });
        }
      } catch {
        // sessionStorage puede fallar en modos restrictivos; el aterrizaje igual sucede.
      }
    })
    .catch(ignorePosthogLoadFailure);
};

/**
 * Initialize PostHog on a PUBLIC, unauthenticated page (the campaign landing).
 * initAnalytics() requires a session, so pre-signup pages would otherwise never
 * boot PostHog and their trackPage/trackEvent calls would no-op. Idempotent with
 * initAnalytics vía loadPosthog (mismo singleton + opciones), so when
 * the visitor later authenticates nothing re-inits. cross_subdomain_cookie keeps
 * the same anonymous distinct_id, so the landing → signup → identify chain links.
 */
export const initPublicAnalytics = (): void => {
  if (typeof window === 'undefined') return;
  if (!isProd() || !POSTHOG_KEY) {
    if (!isProd()) console.log('Analytics: dev mode, PostHog not initialized (public)');
    return;
  }
  void loadPosthog()
    .then(() => {
      analyticsReady = canTrack();
      if (!analyticsReady) {
        pendingCaptures.length = 0;
        return;
      }
      flushPendingCaptures();
      syncActivationRecording();
    })
    .catch(ignorePosthogLoadFailure);
};

// total_confirmed_bookings crudo hacía churn del identify key: cada reserva
// confirmada cambia el count → $set completo re-emitido (seguía siendo el 3er
// evento del proyecto, ~88k/mes proyectado, aun con el key persistido). Para
// segmentar alcanza el orden de magnitud; el bucket solo re-emite al cruzar de
// rango. Ningún insight guardado filtra por el valor numérico exacto (verificado
// jul/2026).
const bookingsBucket = (count: number): string => {
  if (count <= 0) return '0';
  if (count <= 5) return '1-5';
  if (count <= 20) return '6-20';
  if (count <= 100) return '21-100';
  if (count <= 500) return '101-500';
  return '500+';
};

export const identifyUser = (session: SessionI): void => {
  if (!canTrack()) {
    if (!isProd()) console.log('identifyUser (dev):', session.id);
    return;
  }

  // initAnalytics llama identifyUser al marcar el cliente como ready, así que una
  // invocación temprana desde Layout puede salir sin perder el identify definitivo.
  if (!posthog || !analyticsReady) return;

  let paymentMethods = '';
  if (session.vendor?.payment_gateways && session.vendor.payment_gateways.length > 0) {
    paymentMethods = session.vendor.payment_gateways.join(', ');
  }
  if (session.vendor?.is_payment_bank_transfer_enabled) {
    paymentMethods += (paymentMethods ? ', ' : '') + 'Bank Transfer';
  }
  if (session.vendor?.is_payment_cash_enabled) {
    paymentMethods += (paymentMethods ? ', ' : '') + 'Cash';
  }
  paymentMethods = paymentMethods || 'No payment methods available';

  const setProps: Record<string, unknown> = {
    first_name: session.first_name || '',
    last_name: session.last_name || '',
    username: session.vendor?.username || '',
    email: session.email || '',
    name: session.first_name + ' ' + session.last_name,
    phone: session.phone || '',
    role: session.role || '',
    plan: session.vendor?.plan_name || '',
    // Discriminadores del recorrido de activación. Sin estos, cualquier lectura del
    // embudo mezcla poblaciones con caminos distintos: un venue hace 4 pasos de setup
    // y un vendor solo hace 2. Antes no eran segmentables en PostHog (ni person-prop
    // ni event-prop) y por eso el embudo "perdía" al 84% en un paso que ese 84% nunca
    // veía. Van también en cada evento de activación (ver services/analytics/activation).
    is_venue: session.is_venue === true,
    is_experimental: session.is_experimental === true,
    is_staff: session.is_staff === true,
    price_monthly: session.active_plan ? session.active_plan.price_monthly : '',
    log_in: session.connection_type,
    payment_methods: paymentMethods,
    first_service_date_creation: session.first_service_date_creation || '',
    first_confirmed_booking_date: session.first_confirmed_booking_date || '',
    first_basic_storefront_date: session.first_basic_storefront_date || '',
    total_confirmed_bookings: bookingsBucket(session.total_confirmed_bookings || 0),
    vendor_status:
      session.vendor?.vendor_status_history?.sort(
        (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      )[0]?.status || ''
  };

  const setOnceProps: Record<string, unknown> = {};

  // Claim business attribution ($set_once — persists forever, never overwritten).
  // Read from cookie (survives Auth0 redirect) with localStorage fallback.
  let claimEvents: Array<{ id: string; name: string }> = [];
  try {
    const cookieMatch = document.cookie.match(/claim_business=([^;]+)/);
    const claimData = cookieMatch
      ? decodeURIComponent(cookieMatch[1])
      : localStorage.getItem('claim_business');

    if (claimData) {
      const { id, name } = JSON.parse(claimData);
      setOnceProps.acquisition_source = 'claim_business';
      setOnceProps.claimed_business_id = id || '';
      setOnceProps.claimed_business_name = name || '';
      claimEvents = [{ id, name }];

      // Clean up both storage mechanisms
      document.cookie = 'claim_business=;max-age=0;path=/';
      localStorage.removeItem('claim_business');
    }
  } catch {
    // Ignore parse errors
  }

  // Skip the identify (and its $set) when nothing changed since the last call.
  // Always run the claim block below regardless — the claim cookie is one-shot and
  // self-clears, so a claim present forces the identify through.
  const identifyKey = session.id.toString() + '|' + JSON.stringify(setProps);
  if (identifyKey !== getLastIdentifyKey() || claimEvents.length > 0) {
    posthog.identify(session.id.toString(), setProps, setOnceProps);
    setLastIdentifyKey(identifyKey);
  }

  // Fire conversion event (new categorized name + legacy name for existing dashboards)
  claimEvents.forEach(({ id, name }) => {
    posthog?.capture('auth_claim_business_converted', {
      category: 'auth',
      screen: 'App',
      claimed_business_id: id,
      claimed_business_name: name
    });
  });
};

// Low-value, high-volume legacy UI events suppressed to keep event volume under
// control. The meaningful outcomes are already captured by typed feature events
// (e.g. button_save_working_hours_clicked → working_hours_updated). Reversible:
// edit this set to re-enable any of them.
const SUPPRESSED_EVENTS = new Set<string>([
  'sw_active', // service-worker adoption — measurement done
  'menu_item_clicked', // redundant with page_viewed
  'agenda_availability_dropdown_clicked',
  'pending_task_clicked',
  'url_copied'
]);
const isSuppressedEvent = (name: string): boolean =>
  SUPPRESSED_EVENTS.has(name) || name.startsWith('button_');

// De-duplicate rapid re-fires (same event + props within a short window) caused
// by component re-renders or effects with unstable deps — these are never real
// usage (e.g. a *_viewed event firing dozens of times in one render cycle). This
// was inflating volume by ~30-50% on the highest-traffic events.
const DEDUP_WINDOW_MS = 1500;
const recentEvents = new Map<string, number>();
const isDuplicateEvent = (key: string): boolean => {
  const now = Date.now();
  if (recentEvents.size > 300) {
    for (const [k, t] of recentEvents) if (now - t > DEDUP_WINDOW_MS) recentEvents.delete(k);
  }
  const last = recentEvents.get(key);
  recentEvents.set(key, now);
  return last !== undefined && now - last < DEDUP_WINDOW_MS;
};

// Pageview throttle — UNA vista por pantalla por DÍA calendario (hora local del
// navegador). El SPA re-monta cada pantalla cada vez que el vendor vuelve a ella, y
// la home base (/agenda, /) se re-entra decenas de veces por día.
//
// Historia: 1,5 s → 30 min → 3 h para la home base (ago/2026) → persistido en
// localStorage (10/sep/2026). Medido 22/sep–5/oct/2026 con todo eso puesto,
// `$pageview` seguía siendo el 32% de TODO el proyecto (~295k/mes) y el ciclo
// anterior se pasó del millón: PostHog cortó la ingesta 5 días (17–22/sep) y ese
// hueco no se recupera. Colapsando por (dispositivo, día, pantalla) sobraba el 42%
// de las filas — `Agenda` daba 18 vistas por persona en 13 días.
//
// Con "una por día" no se pierde nada de lo que se lee: DAU/WAU, retención y
// growth accounting usan `math: dau` / intervalos de día o semana sobre $pageview,
// y toda pantalla sigue emitiendo su PRIMERA vista de cada día (también tras F5,
// relanzamiento de la PWA o tab reciclado, porque el registro se persiste). Lo que
// cambia: "Pantallas más visitadas" pasa a contar días-pantalla (sigue sirviendo
// para rankear) y las re-entradas a la MISMA pantalla en el día ya no salen en
// Paths ni en el conteo de páginas/sesión de Web Analytics.
const PAGE_DEDUP_STORAGE_KEY = 'analytics_recent_pages';

/** Día calendario LOCAL de un timestamp — la frontera del throttle es medianoche. */
const localDay = (timestamp: number): string => {
  const d = new Date(timestamp);
  return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
};

const loadRecentPages = (): Map<string, number> => {
  try {
    const raw = localStorage.getItem(PAGE_DEDUP_STORAGE_KEY);
    if (!raw) return new Map();
    const today = localDay(Date.now());
    return new Map(
      Object.entries(JSON.parse(raw) as Record<string, number>).filter(
        ([, t]) => typeof t === 'number' && localDay(t) === today
      )
    );
  } catch {
    // Storage lleno, bloqueado (Safari privado) o JSON corrupto: se arranca limpio.
    // El dedup NUNCA puede romper una navegación — el peor caso es un pageview de más.
    return new Map();
  }
};

let recentPages: Map<string, number> | null = null;

const persistRecentPages = (pages: Map<string, number>): void => {
  try {
    localStorage.setItem(PAGE_DEDUP_STORAGE_KEY, JSON.stringify(Object.fromEntries(pages)));
  } catch {
    /* storage lleno o bloqueado: el dedup sigue valiendo en memoria para esta carga */
  }
};

const isRecentPage = (name: string): boolean => {
  const now = Date.now();
  const today = localDay(now);
  if (!recentPages) recentPages = loadRecentPages();
  // Lo de días anteriores ya no puede suprimir nada: se purga para que el registro
  // (memoria + localStorage) no crezca con una PWA abierta de un día para el otro.
  for (const [k, t] of recentPages) if (localDay(t) !== today) recentPages.delete(k);
  const seenToday = recentPages.has(name);
  if (seenToday) return true;
  recentPages.set(name, now);
  persistRecentPages(recentPages);
  return false;
};

// CONVENCIÓN (free tier 1M/mes): NO agregar eventos de alta frecuencia (por
// interacción, scroll, hover o que se disparen en cada render/route) sin un dedup
// o sampling acá. Un evento sin acotar en la pantalla más usada (agenda/home)
// escala a cientos de miles/mes solo. Ver DROP_EVENTS + SUPPRESSED_EVENTS + los
// throttles de arriba. Si sumás uno nuevo caro, sampléalo en before_send.

// Última identificación enviada (id + $set serializado). identifyUser se llama en
// cada cambio de sesión/ruta desde Layout; re-mandar los mismos props era puro
// ruido. Se saltea el identify si nada cambió. PERSISTIDO en localStorage (no en
// memoria): la versión en memoria se reseteaba en CADA carga de página (F5, tab
// nuevo, PWA cold start, vendor que vuelve al día siguiente) → re-emitía un $set
// idéntico en cada cold-start, ~8.6 $set/persona/semana (2º evento más pesado del
// proyecto). Persistido, un retorno con props sin cambios emite 0 $set; sigue
// re-emitiendo cuando algo cambia de verdad (los setProps incluyen counts/estado).
// La clave incluye session.id, así que otro vendor en el mismo browser re-identifica.
const IDENTIFY_KEY_STORAGE = 'analytics_last_identify_key';
const getLastIdentifyKey = (): string => {
  try {
    return localStorage.getItem(IDENTIFY_KEY_STORAGE) || '';
  } catch {
    return '';
  }
};
const setLastIdentifyKey = (key: string): void => {
  try {
    localStorage.setItem(IDENTIFY_KEY_STORAGE, key);
  } catch {
    // localStorage puede fallar en modos restrictivos; el identify igual sucede.
  }
};

export const trackEvent = (name: string, customProps?: Partial<CustomProps>): void => {
  if (isSuppressedEvent(name)) return;
  if (isDuplicateEvent(name + '|' + JSON.stringify(customProps ?? {}))) return;

  const eventData = {
    ...customProps,
    ...getUTMParameters()
  };

  if (canTrack()) {
    captureOrQueue(name, eventData);
  } else {
    console.log('Event:', name, customProps);
  }
};

/**
 * Track a page view. Emite el evento NATIVO `$pageview` de PostHog (antes era el
 * custom `page_viewed`) → desbloquea el producto Web Analytics (tráfico, top
 * pages, bounce, referrers, devices) sin sumar volumen (mismo 1 evento por
 * navegación). Seguimos firmando manual (capture_pageview: false) para control
 * total. Conserva la prop `page` (nombre amigable de la pantalla) además del
 * $pathname/$current_url que PostHog adjunta solo. SOLO Flash usa el nativo;
 * nightwing/lex siguen con sus eventos custom.
 */
export const trackPage = (name: string, customProps?: Partial<CustomProps>): void => {
  if (isRecentPage(name)) return;

  const eventData = {
    page: name,
    ...customProps,
    ...getUTMParameters()
  };

  if (canTrack()) {
    captureOrQueue('$pageview', eventData);
  } else {
    console.log('Page Event: $pageview', { page: name, ...customProps });
  }
};

// ── Session recording — scoped to the activation funnel ONLY ──────────────────
// We record sessions only during onboarding / setup 1 / setup 2 (highest-value,
// low-volume → stays within the free 5k recordings/month tier). The flow root
// components call these on mount/unmount via the useSessionRecording hook.
export const startActivationRecording = (): void => {
  activationRecordingRequested = true;
  syncActivationRecording();
};
export const stopActivationRecording = (): void => {
  activationRecordingRequested = false;
  if (!posthog) return;
  posthog.stopSessionRecording();
};

// Muestra del tráfico FRÍO de /comenzar. La grabación de la landing arranca en la
// primera interacción con el form (ver ensureRecording en CampaignLanding), así que
// del ~87% que ve la página y se va sin tocar nada no había NI UN replay — y ahí
// está el grueso de la pérdida (la mitad de los humanos se va en <3s con 0% de
// scroll, y NO es performance: el bounce es peor cuanto más rápido pinta).
//
// El rate está atado al free tier: 5k grabaciones/mes, run-rate actual ~1.7k. Al 25%
// del tráfico pago que monta la landing la muestra suma ~1.6k/mes → ~3.3k totales,
// con colchón. NO subirlo sin volver a mirar el consumo: sin tarjeta cargada el tope
// es DURO y PostHog corta la ingesta al cruzarlo (nos pasó con eventos el 23/jul).
const COLD_LANDING_SAMPLE_RATE = 0.25;

export const startSampledLandingRecording = (): boolean => {
  // Las precargas fantasma del webview de IG montan ocultas: grabarlas es quemar
  // cuota en sesiones sin un solo píxel en pantalla.
  if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return false;
  if (Math.random() >= COLD_LANDING_SAMPLE_RATE) return false;
  startActivationRecording();
  return true;
};

// ── Error tracking ────────────────────────────────────────────────────────────
// Manual exception capture (for caught errors / React error boundaries). Unhandled
// errors are auto-captured via startExceptionAutocapture(). Excludes dev + god-mode.
export const captureException = (error: unknown, context?: Record<string, unknown>): void => {
  if (!canTrack()) {
    if (!isProd()) console.error('captureException (dev):', error, context);
    return;
  }
  posthog?.captureException(error, context);
};

// Step timing utilities for tracking duration
interface StepTimestamp {
  stepKey: string;
  timestamp: number;
}

const STEP_TIMING_STORAGE_KEY = 'analytics_step_timing';

/**
 * Records the start time for a step
 * @param stepKey - Unique identifier for the step (e.g., "onboarding_step_1", "setup1_step_2")
 */
export const startStepTiming = (stepKey: string): void => {
  const timestamp: StepTimestamp = {
    stepKey,
    timestamp: Date.now()
  };

  try {
    localStorage.setItem(`${STEP_TIMING_STORAGE_KEY}_${stepKey}`, JSON.stringify(timestamp));
  } catch (error) {
    console.warn('Failed to save step timing:', error);
  }
};

/**
 * Calculates the duration in seconds since the step was started
 * @param stepKey - Unique identifier for the step
 * @returns Duration in seconds, or null if no start time was recorded
 */
export const getStepDuration = (stepKey: string): number | null => {
  try {
    const stored = localStorage.getItem(`${STEP_TIMING_STORAGE_KEY}_${stepKey}`);
    if (!stored) return null;

    const { timestamp }: StepTimestamp = JSON.parse(stored);
    const durationMs = Date.now() - timestamp;
    const durationSeconds = Math.round(durationMs / 1000);

    return durationSeconds;
  } catch (error) {
    console.warn('Failed to get step duration:', error);
    return null;
  }
};

/**
 * Clears all timing data for a flow (e.g., all onboarding steps)
 * @param flowPrefix - Prefix for the flow (e.g., "onboarding", "setup1", "setup2")
 */
export const clearFlowTiming = (flowPrefix: string): void => {
  try {
    const keys = Object.keys(localStorage);
    keys.forEach((key) => {
      if (key.startsWith(`${STEP_TIMING_STORAGE_KEY}_${flowPrefix}`)) {
        localStorage.removeItem(key);
      }
    });
  } catch (error) {
    console.warn('Failed to clear flow timing:', error);
  }
};
